// ============================================
// distributor.js — Cliente para el programa de distribuidoras
// Physiology New Look · Medusa v2
// ============================================

import { MEDUSA_API_URL, MEDUSA_API_KEY } from './config';

const baseHeaders = (token) => ({
  'Content-Type': 'application/json',
  'x-publishable-api-key': MEDUSA_API_KEY,
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
});

const handle = async (res) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.message || `Error ${res.status}`);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
};

// POST público: envía una solicitud para ser distribuidora.
export async function applyDistributor(payload, token) {
  const res = await fetch(`${MEDUSA_API_URL}/store/distributor-applications`, {
    method: 'POST',
    credentials: 'include',
    headers: baseHeaders(token),
    body: JSON.stringify(payload),
  });
  return handle(res);
}

// GET autenticado: ¿soy distribuidora? + estado de mi última solicitud.
export async function getMyDistributorStatus(token) {
  const res = await fetch(`${MEDUSA_API_URL}/store/customers/me/distributor`, {
    method: 'GET',
    credentials: 'include',
    headers: baseHeaders(token),
  });
  return handle(res);
}

// GET autenticado (solo distribuidoras): datos directos del fundador.
// El backend responde 403 si la clienta no es distribuidora.
export async function getFounderContact(token) {
  const res = await fetch(`${MEDUSA_API_URL}/store/customers/me/founder-contact`, {
    method: 'GET',
    credentials: 'include',
    headers: baseHeaders(token),
  });
  return handle(res);
}

// GET autenticado (solo distribuidoras): ticket firmado para abrir el chat en
// la bandeja privada de distribución del servidor de atención.
export async function getChatTicket(token) {
  const res = await fetch(`${MEDUSA_API_URL}/store/customers/me/chat-ticket`, {
    method: 'GET',
    credentials: 'include',
    headers: baseHeaders(token),
  });
  return handle(res);
}

// GET autenticado: progreso acumulado en la temporada actual + tier alcanzado
// y siguiente. Payload:
//   {
//     accumulated, currency_code,
//     current_tier: { id, label, min_amount } | null,
//     next_tier:    { id, label, min_amount } | null,
//     season:       { label, start, end },
//     paid_orders_count,
//   }
export async function getMyDistributorProgress(token) {
  const res = await fetch(
    `${MEDUSA_API_URL}/store/customers/me/distributor/progress`,
    {
      method: 'GET',
      credentials: 'include',
      headers: baseHeaders(token),
    }
  );
  return handle(res);
}

// GET autenticado: últimas N órdenes del cliente con fulfillment y tracking.
// Si pides limit=1 te devuelve un array de 1 (o vacío). Para uso simple como
// "última orden" → recent[0].
export async function getMyRecentOrders(token, limit = 5) {
  const fields =
    '*items,*shipping_methods,*fulfillments,*fulfillments.labels,*fulfillments.shipping_option,+payment_collections.status';
  const res = await fetch(
    `${MEDUSA_API_URL}/store/orders?limit=${limit}&order=-created_at&fields=${encodeURIComponent(fields)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: baseHeaders(token),
    }
  );
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return data?.orders || [];
}

// Backwards-compat: alias antiguo, devuelve sólo la última.
export async function getMyLatestOrder(token) {
  const orders = await getMyRecentOrders(token, 1);
  return orders[0] || null;
}

// GET autenticado: una orden por id, con todo lo que necesita la página de
// detalle (items, dirección de envío, fulfillments+labels para tracking,
// totales y metadata de factura). El endpoint nativo /store/orders/:id ya
// está scoped al cliente autenticado en Medusa v2.
export async function getOrderById(token, orderId) {
  const fields = [
    '*items',
    '*shipping_address',
    '*shipping_methods',
    '*fulfillments',
    '*fulfillments.labels',
    '*fulfillments.shipping_option',
    '+fulfillment_status',
    '+payment_status',
    '+payment_collections.status',
    '+metadata',
  ].join(',');
  const res = await fetch(
    `${MEDUSA_API_URL}/store/orders/${orderId}?fields=${encodeURIComponent(fields)}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: baseHeaders(token),
    }
  );
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return data?.order || null;
}

// ─── Facturación CFDI vía Facturapi ─────────────────────────────────

// GET: ¿esta orden ya tiene factura emitida? Devuelve null si no.
export async function getOrderInvoice(token, orderId) {
  const res = await fetch(
    `${MEDUSA_API_URL}/store/orders/${orderId}/invoice`,
    {
      method: 'GET',
      credentials: 'include',
      headers: baseHeaders(token),
    }
  );
  if (res.status === 404) return null;
  if (!res.ok) return null;
  const data = await res.json().catch(() => ({}));
  return data?.invoice || null;
}

// POST: emite la factura. receptor = { legal_name, tax_id, tax_system, email,
// address: { zip } }. use = código CFDI (G03, G01, P01…).
export async function requestInvoice(token, orderId, payload) {
  const res = await fetch(
    `${MEDUSA_API_URL}/store/orders/${orderId}/invoice`,
    {
      method: 'POST',
      credentials: 'include',
      headers: baseHeaders(token),
      body: JSON.stringify(payload),
    }
  );
  return handle(res);
}

// Descarga el archivo de factura (pdf | xml) via fetch con los headers
// auth (publishable key + bearer). Una navegación directa con <a href>
// fallaría porque el browser no envía esos headers. Crea un blob URL y
// dispara la descarga al instante.
export async function downloadInvoiceFile(token, orderId, kind, filename) {
  const path = kind === 'xml' ? 'xml' : 'pdf';
  const res = await fetch(
    `${MEDUSA_API_URL}/store/orders/${orderId}/invoice/${path}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: baseHeaders(token),
    }
  );
  if (!res.ok) {
    const msg = `No se pudo descargar la factura (${res.status}).`;
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || `factura-${orderId}.${path}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // El revoke se difiere para que el navegador alcance a iniciar la descarga.
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

// GET público: top de productos más solicitados (suma de quantity en order
// items pagadas). distributors_only=true filtra por órdenes de distribuidoras
// únicamente, para que el ranking refleje "lo que más mueve la red de socias".
export async function getPopularProducts({ limit = 10, distributorsOnly = true } = {}) {
  const qs = new URLSearchParams({
    limit: String(limit),
    distributors_only: distributorsOnly ? 'true' : 'false',
  });
  const res = await fetch(`${MEDUSA_API_URL}/store/products/popular?${qs}`, {
    method: 'GET',
    credentials: 'include',
    headers: baseHeaders(),
  });
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return data?.products || [];
}

// GET público: tiers activos (para mostrar progreso de regalos en landing/carrito).
export async function getDistributorTiers() {
  const res = await fetch(`${MEDUSA_API_URL}/store/distributor-tiers`, {
    method: 'GET',
    credentials: 'include',
    headers: baseHeaders(),
  });
  return handle(res);
}

// GET público: precios del price list "Distribuidores" para los variant_ids dados.
// Se usa para mostrar "precio socio" lado a lado con el precio público en la tienda,
// como gancho al programa. Devuelve un mapa { [variant_id]: { amount, currency_code } }.
export async function getDistributorPreviewPrices(variantIds) {
  if (!variantIds?.length) return {};
  const qs = new URLSearchParams();
  variantIds.forEach((id) => qs.append('variant_ids[]', id));
  const res = await fetch(
    `${MEDUSA_API_URL}/store/distributor-preview-prices?${qs.toString()}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: baseHeaders(),
    }
  );
  if (!res.ok) return {};
  const data = await res.json().catch(() => ({}));
  return data?.prices || {};
}

// Helper: enriquece una lista de productos de Medusa con el precio socio.
// Inyecta variant.distributor_amount (Number, en major) en cada variante si existe.
export async function attachDistributorPrices(products) {
  if (!Array.isArray(products) || products.length === 0) return products;
  const variantIds = products.flatMap((p) =>
    (p.variants || []).map((v) => v.id).filter(Boolean)
  );
  if (!variantIds.length) return products;
  const prices = await getDistributorPreviewPrices(variantIds);
  for (const p of products) {
    for (const v of p.variants || []) {
      const entry = prices[v.id];
      if (entry) {
        // Medusa v2 devuelve amounts en pesos (unidades mayores).
        v.distributor_amount = Number(entry.amount);
      }
    }
  }
  return products;
}
